import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from './StatusBadge';

function safeJsonParse(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : { value };
    } catch (_) {
      return { value };
    }
  }
  return { value: String(value) };
}

function formatKey(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(v) {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch (_) {
      return String(v);
    }
  }
  return String(v);
}

function computeChanges(log) {
  const oldObj = safeJsonParse(log.old_values) || {};
  const newObj = safeJsonParse(log.new_values) || {};

  if (log.action === 'created' || Object.keys(oldObj).length === 0) {
    return Object.entries(newObj).map(([k, v]) => ({
      key: k,
      label: formatKey(k),
      before: null,
      after: v
    }));
  }

  const keys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]));
  const changed = [];
  keys.forEach((k) => {
    const before = oldObj[k];
    const after = newObj[k];
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      changed.push({ key: k, label: formatKey(k), before, after });
    }
  });
  return changed;
}

function createDefaultFormatAction(t) {
  return (action) => {
    const map = {
      created: t('auditTimeline.actions.created'),
      updated: t('auditTimeline.actions.updated'),
      deleted: t('auditTimeline.actions.deleted'),
      converted: t('auditTimeline.actions.converted'),
      created_from_lead: t('auditTimeline.actions.createdFromLead'),
      created_direct: t('auditTimeline.actions.createdDirect'),
      visa_updated: t('auditTimeline.actions.visaUpdated'),
      email_sent: t('auditTimeline.actions.emailSent'),
      email_reply_sent: t('auditTimeline.actions.emailReplySent'),
      email_received: t('auditTimeline.actions.emailReceived'),
      zoom_generated: t('auditTimeline.actions.zoomLinkGenerated'),
      zoom_synced: t('auditTimeline.actions.zoomDataSynced'),
      zoom_webhook: t('auditTimeline.actions.zoomWebhook'),
      generated: t('auditTimeline.actions.agreementGenerated'),
      signed_uploaded: t('auditTimeline.actions.signedCopyUploaded'),
      verification_updated: t('auditTimeline.actions.verificationUpdated'),
      mark_paid: t('auditTimeline.actions.markedAsPaid'),
      refund_created: t('auditTimeline.actions.refundCreated'),
      refund_updated: t('auditTimeline.actions.refundUpdated'),
      refund_deleted: t('auditTimeline.actions.refundDeleted'),
      bulk_assigned: t('auditTimeline.actions.bulkAssigned'),
      bulk_status_update: t('auditTimeline.actions.bulkStatusUpdate'),
      bulk_deleted: t('auditTimeline.actions.bulkDeleted'),
      status_updated_from_meeting: t('auditTimeline.actions.statusUpdatedMeeting'),
      uploaded: t('auditTimeline.actions.uploaded'),
      deactivated: t('auditTimeline.actions.deactivated')
    };
    return map[action] || String(action || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
}

function createDefaultFormatEntityType(t) {
  return (type) => {
    const map = {
      lead: t('auditTimeline.entityTypes.lead'),
      student: t('auditTimeline.entityTypes.student'),
      meeting: t('auditTimeline.entityTypes.meeting'),
      activity: t('auditTimeline.entityTypes.activity'),
      document: t('auditTimeline.entityTypes.document'),
      application: t('auditTimeline.entityTypes.application'),
      user: t('auditTimeline.entityTypes.user'),
      interview: t('auditTimeline.entityTypes.interview'),
      fee: t('auditTimeline.entityTypes.fee'),
      agreement: t('auditTimeline.entityTypes.agreement'),
      email_template: t('auditTimeline.entityTypes.emailTemplate')
    };
    return map[type] || String(type || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
}

function initials(name, systemInitial = 'S') {
  const s = String(name || '').trim();
  if (!s) return systemInitial;
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || systemInitial;
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
  return `${first}${last}`.toUpperCase();
}

export default function AuditTimeline({
  logs,
  showEntity = true,
  maxCollapsedChanges = 6,
  formatAction,
  formatEntityType,
  filterLog
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const { t } = useTranslation();

  const defaultFormatActionT = useMemo(() => createDefaultFormatAction(t), [t]);
  const defaultFormatEntityTypeT = useMemo(() => createDefaultFormatEntityType(t), [t]);
  const formatActionResolved = formatAction ?? defaultFormatActionT;
  const formatEntityTypeResolved = formatEntityType ?? defaultFormatEntityTypeT;

  const items = useMemo(() => {
    const src = Array.isArray(logs) ? logs : [];
    const filtered = filterLog ? src.filter(filterLog) : src;
    return filtered.map((log) => ({ log, changes: computeChanges(log) }));
  }, [logs, filterLog]);

  if (!items.length) {
    return (
      <div className="empty-state">
        <p>{t('auditTimeline.noHistory')}</p>
      </div>
    );
  }

  return (
    <div className="timeline">
      {items.map(({ log, changes }) => {
        const who = log.changed_by_name || t('common.system');
        const when = log.changed_at ? new Date(log.changed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '';
        const key = log.log_id || `${log.entity_type}-${log.entity_id}-${log.changed_at || ''}-${log.action || ''}`;
        const isOpen = expanded.has(String(key));
        const list = isOpen ? changes : changes.slice(0, maxCollapsedChanges);
        const hiddenCount = Math.max(0, changes.length - list.length);

        return (
          <div className="timeline-item" key={key} style={{ display: 'flex', gap: 16, padding: '16px 20px' }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'var(--bg-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text)',
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 800
            }}>
              {initials(who, t('common.system').charAt(0))}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <StatusBadge status={formatActionResolved(log.action)} />
                  {showEntity && (
                    <span className="badge" style={{ background: 'var(--bg-secondary)', fontSize: 11 }}>
                      {formatEntityTypeResolved(log.entity_type)}
                    </span>
                  )}
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>{who}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {when}
                </div>
              </div>

              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {list.length > 0 ? (
                  list.map((c) => (
                    <div key={c.key} style={{ fontSize: 13, color: 'var(--text-secondary)', wordBreak: 'break-word' }}>
                      <strong style={{ color: 'var(--text)' }}>{c.label}:</strong>{' '}
                      {c.before === null && log.action === 'created'
                        ? formatValue(c.after)
                        : `${formatValue(c.before)} → ${formatValue(c.after)}`}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('auditTimeline.noFieldChanges')}</div>
                )}
              </div>

              {changes.length > maxCollapsedChanges && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      const k = String(key);
                      if (next.has(k)) next.delete(k);
                      else next.add(k);
                      return next;
                    });
                  }}
                >
                  {isOpen ? t('auditTimeline.showLess') : t('auditTimeline.showMore', { count: hiddenCount })}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

